import { ref } from 'vue'
import { useWebNotification, useVibrate } from '@vueuse/core'
import { instanceI18n } from '@/plugins/i18n'
import { useNotificationSettingsStore } from '@/stores/useNotificationSettingsStore'

const soundEnabled = ref(true)

const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

const isDocumentVisible = (): boolean => {
  return document.visibilityState === 'visible'
}

let audioContext: AudioContext | null = null
let playAudio: ((frequency: number, duration: number, volume?: number) => void) | null = null
let isAudioInitialized = false

// Sonido simple de beep para notificaciones (WAV base64)
const createBeepSound = (): string => {
  // Un beep corto y simple en formato WAV
  return 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBCqF0fPTgjMGHm7A7+OZSA0PVqzn77BdGAU+ltryxHInBTB+zPLaizsIGGS57eeXSw0PUqfj8LRjHAY2kdbyzHksBSJ2yPDajkALEl2z6eumUxQJRp/g8LxsIQQqhdDz04M0Bh1swO/jmEgODlOr5u+wXRgFPpbZ8sSyJwUue8zx2oo7CBdku+3nlUoND1Km4u+zYhwGNZHW8st5LAQhdcnu2Y5ACxNdt+jqplQUCUSe4PC8aiEEKYPP89OBNQYdbL/v45dJDg1TqubwsV4YBT6W2fLEsSYFLnrM8dmKOwgXY7zs55VKDg1SpeH0tGMcBTOQ1fLKeSwEIHPH79qOQQwSXbbp66ZUFApDnN/wu2YgBCmB0PPTgDQGHmu+7+OXSw4OUKrl8LJSGA=='
}

const initAudioContext = () => {
  if (audioContext && isAudioInitialized) return playAudio

  try {
    if (!audioContext) {
      audioContext = new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
    }

    if (audioContext.state === 'suspended') {
      audioContext
        .resume()
        .then(() => {
          isAudioInitialized = true
        })
        .catch((err) => {
          console.error('Error al reanudar el AudioContext:', err)
        })
    } else {
      isAudioInitialized = true
    }

    playAudio = (frequency: number, duration: number, volume: number = 0.3) => {
      if (!audioContext) return

      if (audioContext.state === 'suspended') {
        audioContext
          .resume()
          .then(() => {
            playTone(frequency, duration, volume)
          })
      } else {
        playTone(frequency, duration, volume)
      }
    }

    return playAudio
  } catch (error) {
    console.error('Error initializing AudioContext:', error)
    return null
  }
}

const playTone = (frequency: number, duration: number, volume: number) => {
  if (!audioContext) return

  try {
    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(audioContext.destination)

    oscillator.frequency.value = frequency
    oscillator.type = 'sine'

    gainNode.gain.setValueAtTime(volume, audioContext.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration)

    oscillator.start(audioContext.currentTime)
    oscillator.stop(audioContext.currentTime + duration)
  } catch (error) {
    console.error('Error playing tone:', error)
  }
}

const playNotificationSound = (type: 'message' | 'alert' | 'transfer' = 'message'): void => {
  if (!soundEnabled.value) return

  // Primero intentar con HTML5 Audio para mayor compatibilidad.
  try {
    const audio = new Audio(createBeepSound())
    audio.volume = 0.5
    audio
      .play()
      .then(() => {})
      .catch(() => {
        // Si HTML5 Audio falla, intentar con AudioContext
        playWithAudioContext(type)
      })
  } catch {
    playWithAudioContext(type)
  }
}

const playWithAudioContext = (type: 'message' | 'alert' | 'transfer') => {
  const audio = playAudio || initAudioContext()
  if (!audio) return

  if (audioContext?.state === 'suspended') {
    audioContext
      .resume()
      .then(() => {
        playSound(audio, type)
      })
  } else {
    playSound(audio, type)
  }
}

const playSound = (
  audio: (frequency: number, duration: number, volume?: number) => void,
  type: 'message' | 'alert' | 'transfer',
) => {
  try {
    switch (type) {
      case 'message':
        audio(800, 0.2, 0.4)
        break
      case 'alert':
        audio(1000, 0.3, 0.5)
        setTimeout(() => audio(800, 0.2, 0.4), 150)
        break
      case 'transfer':
        audio(900, 0.15, 0.45)
        setTimeout(() => audio(1100, 0.15, 0.45), 100)
        setTimeout(() => audio(900, 0.15, 0.45), 200)
        break
    }
  } catch (error) {
    console.error('Error playing sound:', error)
  }
}

export const useNotifications = () => {
  const t = instanceI18n.global.t
  // Se resuelve aqui y no dentro de cada aviso: los avisos corren desde
  // handlers del WebSocket, fuera de cualquier setup, y este composable si se
  // crea dentro de uno.
  const notificationSettings = useNotificationSettingsStore()
  const { isSupported: isVibrateSupported, vibrate } = useVibrate()
  const { isSupported, show } = useWebNotification({
    title: t('notifications.app_title'),
    dir: 'auto',
    lang: String(instanceI18n.global.locale),
    renotify: false,
    tag: 'whatsapp-notification',
    icon: '/favicon.ico',
  })

  const requestPermission = async (): Promise<boolean> => {
    if (!isSupported.value) {
      return false
    }

    if (!('Notification' in window)) {
      return false
    }

    if (Notification.permission === 'granted') {
      return true
    }

    if (Notification.permission === 'denied') {
      return false
    }

    try {
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      return false
    }
  }

  const showDesktopNotification = async (options: {
    title: string
    body: string
    tag?: string
    data?: Record<string, unknown>
  }) => {
    if (!isSupported.value) return

    const hasPermission = await requestPermission()

    if (!hasPermission) return

    show({
      title: options.title,
      body: options.body,
      tag: options.tag,
      requireInteraction: false,
      silent: true,
    }).then((notification) => {
      if (notification) {
        notification.onclick = (event) => {
          event.preventDefault()
          window.focus()
          notification.close()

          if (options.data?.chatId) {
            const event = new CustomEvent('notification-click', {
              detail: { chatId: options.data.chatId },
            })
            window.dispatchEvent(event)
          }
        }

        setTimeout(() => {
          notification.close()
        }, 5000)
      }
    })
  }

  /**
   * `isQueued`: el mensaje es de un chat que espera en la cola. El backend los
   * reparte a toda la campana —para que la lista y la vista previa se muevan
   * en todos los Opers, no porque el chat sea de alguien—, asi que estos si
   * respetan el interruptor de la cola.
   */
  const notifyNewMessage = async (options: {
    contactName: string
    messagePreview: string
    chatId: string
    isCurrentChat?: boolean
    isQueued?: boolean
  }) => {
    if (options.isCurrentChat && isDocumentVisible()) return

    const soundAllowed = !options.isQueued || notificationSettings.queuedChatSound
    const desktopAllowed = !options.isQueued || notificationSettings.queuedChatDesktop
    const shouldNotifyDesktop =
      desktopAllowed && (!isDocumentVisible() || !isMobileDevice())
    const shouldVibrate = soundAllowed && isMobileDevice()

    if (soundAllowed) {
      playNotificationSound('message')
    }

    if (shouldVibrate && isVibrateSupported.value) {
      vibrate([100, 50, 100])
    }

    if (shouldNotifyDesktop) {
      showDesktopNotification({
        title: t('notifications.new_message_title', { contactName: options.contactName }),
        body: options.messagePreview,
        tag: `chat-${options.chatId}`,
        data: { chatId: options.chatId },
      })
    }
  }

  /**
   * `isQueued` distingue los dos chats nuevos que existen: el que entra a la
   * cola de la campana —lo ven todos los agentes y es el que se puede
   * silenciar desde la campana— y el que llega ya asignado a este agente
   * (respuesta a una plantilla), que es trabajo suyo y siempre avisa.
   */
  const notifyNewChat = async (options: {
    contactName: string
    contactPhone: string
    chatId: string
    isQueued?: boolean
  }) => {
    const soundAllowed = !options.isQueued || notificationSettings.queuedChatSound
    const desktopAllowed = !options.isQueued || notificationSettings.queuedChatDesktop

    if (soundAllowed) {
      playNotificationSound('alert')

      // La vibracion es el sonido del movil: va con el mismo interruptor.
      if (isMobileDevice() && isVibrateSupported.value) {
        vibrate([200, 100, 200, 100, 200])
      }
    }

    if (desktopAllowed && (!isDocumentVisible() || !isMobileDevice())) {
      showDesktopNotification({
        title: t('notifications.new_chat_title'),
        body: t('notifications.new_chat_body', {
          contactName: options.contactName,
          contactPhone: options.contactPhone,
        }),
        tag: `new-chat-${options.chatId}`,
        data: { chatId: options.chatId },
      })
    }
  }

  /**
   * Un chat volvio a la cola porque otro agente cerro sesion. El backend lo
   * anuncia a toda la campana, asi que para quien lo recibe es lo mismo que un
   * chat nuevo en cola: trabajo disponible que no es suyo todavia. Va con el
   * mismo interruptor. Solo suena —este camino no levanta aviso de escritorio—.
   */
  const notifyQueuedChatAvailable = () => {
    if (!notificationSettings.queuedChatSound) return

    playNotificationSound('alert')
  }

  const notifyTransferRequest = async (options: {
    fromUserName: string
    contactName: string
    chatId: string
  }) => {
    playNotificationSound('transfer')

    if (isMobileDevice() && isVibrateSupported.value) {
      vibrate([300, 200, 300])
    }

    showDesktopNotification({
      title: t('notifications.transfer_request_title'),
      body: t('notifications.transfer_request_body', {
        fromUserName: options.fromUserName,
        contactName: options.contactName,
      }),
      tag: `transfer-${options.chatId}`,
      data: { chatId: options.chatId },
    })
  }

  const notifySystemAction = async (options: {
    title: string
    body: string
    chatId?: string
    tag?: string
  }) => {
    playNotificationSound('alert')

    if (isMobileDevice() && isVibrateSupported.value) {
      vibrate([200, 100, 200])
    }

    showDesktopNotification({
      title: options.title,
      body: options.body,
      tag: options.tag || (options.chatId ? `system-${options.chatId}` : undefined),
      data: options.chatId ? { chatId: options.chatId } : undefined,
    })
  }

  const notifyIncomingCall = async (options: { phoneNumber?: string; callId?: string }) => {
    if (isDocumentVisible() && document.hasFocus()) return

    playNotificationSound('alert')

    if (isMobileDevice() && isVibrateSupported.value) {
      vibrate([300, 150, 300, 150, 300])
    }

    showDesktopNotification({
      title: t('notifications.incoming_call_title'),
      body: options.phoneNumber
        ? t('notifications.incoming_call_body_with_number', { phoneNumber: options.phoneNumber })
        : t('notifications.incoming_call_body'),
      tag: options.callId ? `incoming-call-${options.callId}` : 'incoming-call',
    })
  }

  const notifyTransferRejected = async (options: { contactName: string; chatId: string }) => {
    playNotificationSound('alert')

    if (isMobileDevice() && isVibrateSupported.value) {
      vibrate([200, 100, 200])
    }

    showDesktopNotification({
      title: t('notifications.transfer_rejected_title'),
      body: t('notifications.transfer_rejected_body', { contactName: options.contactName }),
      tag: `transfer-rejected-${options.chatId}`,
      data: { chatId: options.chatId },
    })
  }

  const toggleSound = () => {
    soundEnabled.value = !soundEnabled.value
  }

  const initializeAudio = () => {
    initAudioContext()
    if (audioContext && audioContext.state === 'suspended') {
    }
  }

  return {
    isSupported,
    soundEnabled,
    isVibrateSupported,
    isMobile: isMobileDevice(),
    requestPermission,
    notifyNewMessage,
    notifyNewChat,
    notifyQueuedChatAvailable,
    notifyTransferRequest,
    notifyTransferRejected,
    notifySystemAction,
    notifyIncomingCall,
    playNotificationSound,
    vibrate,
    toggleSound,
    initializeAudio,
  }
}

